/**
 * NewJobModal — create a job from the Operations screen (Quick 260614-s5w / #8).
 *
 * Phase 9 scoped the dispatcher console to viewing/assigning jobs but NOT
 * creating them — there was no "New Job" affordance anywhere in web-dispatch.
 * This modal closes that gap: POST /v1/jobs (CREATE_JOB) with a job type
 * (required) + optional site, driver, and scheduled time. Operator is derived
 * server-side / left null (the create API treats it as optional; SiteSummary
 * carries no operatorId to forward).
 *
 * On success the jobs query is invalidated so the new PENDING job appears in the
 * queue immediately (no wait for the 30s poll).
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, useApiOpts, useApiMutation } from '@lodex-pro/web-shared';
import type { SiteSummary } from '@lodex-pro/shared';
import {
  lockedExpressCustomerId,
  onlyExpressCustomers,
  onlyExpressPickupSites,
  useExpressCustomerState,
} from '../../lib/express-customer';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Combobox } from '../../components/Combobox';
import { useJobTypesForCustomer } from './use-job-types';
import { useDrivers } from './use-drivers';
import { useOperators } from './use-operators';
import { useTanks } from './use-tanks';
import { useDisposalRecommendations } from './use-disposal-recommendations';
import {
  buildDestinationOptions,
  destinationPayload,
  estimateNote,
  excludedChoice,
  MIN_OVERRIDE_REASON,
} from './destination-options';
import { useSites } from '../geofences/use-sites';
import {
  useTankBatteries,
  useWellSearch,
  useHaulerOperators,
  useWellDetail,
  type WellDetail,
} from '../sites-operators/hooks';
// 86bc7quft — the debounce helper the bulk-assign well search already uses, and the
// server's page cap, which is what lets a caller tell a truncated result from a
// complete one (OPEN-053).
import { useDebounced } from '../sites-operators/CatalogSearchModal';
import { WELL_SEARCH_MAX_LIMIT } from '@lodex-pro/shared/wells-paging';
import { useAssignments } from '../requestors/hooks';
import {
  assignmentTargetFor,
  selectableRequestors,
  toggleChoice,
  requestorPayload,
  requesterFieldsLocked,
} from '../requestors/ticket-entry';

export interface NewJobModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

interface CreateJobBody {
  jobTypeId: number;
  assignedDriverId?: string;
  operatorId?: string;
  siteId?: string;
  // Phase 24 / Task 24.10 (DEC-101) — new-model pickup; the server resolves the
  // hauler operator + generates the HAU-format HaulId from this battery.
  tankBatteryId?: string;
  // quick 260717-kme — new-model pickup at a STANDALONE WELL (a well with a
  // directly-attached tank, no battery). Peer of tankBatteryId; the server
  // resolves the operator + HaulId from the well.
  pickupWellId?: string;
  tankId?: string;
  plannedDisposalFacilityId?: string;
  // Phase 26 / Task 26.3 — a drop-off at one of the company's own locations instead.
  plannedDropoffHaulerLocationId?: string;
  // SWD destination A1 — or a catalog disposal well the company authorized.
  plannedDropoffDisposalWellId?: string;
  // Phase 28 (DEC-331 D-14 A) — why an excluded destination was chosen anyway.
  disposalOverrideReason?: string;
  scheduledAt?: string;
  // DEC-146 (Phase 17) — dispatcher can flag a job urgent at creation.
  isUrgent?: boolean;
  // Quick 260722 — batch-create N identical jobs (web only). Default 1.
  quantity?: number;
  // Legacy "New Request" parity — optional requester contact + reference id.
  requesterName?: string;
  requesterPhone?: string;
  requesterEmail?: string;
  referenceId?: string;
  // OPEN-053 — one of the pickup location's two assigned requestors. When present the
  // server snapshots THEIR contact details into requester_*, so the free-text fields above
  // are locked in the UI rather than sent alongside.
  requestorId?: string;
  // #44 — free-text dispatcher notes (jobs.dispatcher_notes), stored as typed.
  dispatcherNotes?: string;
}

/** Max jobs a single batch-create can mint (mirrors the server cap). */
export const MAX_JOB_QUANTITY = 25;

/**
 * Normalize a raw "Number of loads" field value to an integer in [1, MAX].
 * Empty/invalid/<1 → 1; above MAX → MAX; fractions floored.
 */
export function clampQuantity(raw: string): number {
  return Math.min(Math.max(Math.floor(Number(raw) || 1), 1), MAX_JOB_QUANTITY);
}

/**
 * DEC-176 (ClickUp 86bb0yq4b) — decide the job type to preselect on modal open.
 * Returns the id to select, or null to leave the field alone. Extracted as a pure
 * helper so the preselect logic is unit-testable without mounting the Radix modal.
 * NEVER clobbers a non-empty (touched) selection; skips a default that is not in the
 * selectable list (LINE_ITEM / out-of-scope for the chosen customer are excluded upstream).
 */
export function preselectDefaultJobTypeId(
  selectable: ReadonlyArray<{ id: number; isDefaultForDispatch: boolean }>,
  currentJobTypeId: string,
): string | null {
  if (currentJobTypeId !== '') return null;
  const def = selectable.find((jt) => jt.isDefaultForDispatch);
  return def === undefined ? null : String(def.id);
}

/**
 * Collect the optional legacy "New Request" contact + reference fields into a
 * CreateJobBody partial: each value is trimmed and INCLUDED only when non-empty
 * (the server treats every one as optional). Extracted as a pure helper so the
 * body-assembly is unit-testable without mounting the Radix-based modal.
 */
export function collectRequestMetadata(fields: {
  requesterName: string;
  requesterPhone: string;
  requesterEmail: string;
  referenceId: string;
}): Pick<CreateJobBody, 'requesterName' | 'requesterPhone' | 'requesterEmail' | 'referenceId'> {
  const out: Pick<
    CreateJobBody,
    'requesterName' | 'requesterPhone' | 'requesterEmail' | 'referenceId'
  > = {};
  const rn = fields.requesterName.trim();
  const rp = fields.requesterPhone.trim();
  const re = fields.requesterEmail.trim();
  const ref = fields.referenceId.trim();
  if (rn) out.requesterName = rn;
  if (rp) out.requesterPhone = rp;
  if (re) out.requesterEmail = re;
  if (ref) out.referenceId = ref;
  return out;
}

// ── Split Customer + Site pickup (two linked, always-enabled fields) ───────────
// Per dispatcher request the single combined "site or customer" search is split
// into two ALWAYS-ENABLED, linked fields: Customer (hauler_operators) then Site
// (tank batteries + standalone wells + legacy company_sites). Selecting a Site
// autofills its Customer; selecting a Customer filters the Site list to that
// customer. Neither field is gated on the other. Extracted as pure helpers so
// they can be unit-tested without opening the Radix combobox (needs
// ResizeObserver, unavailable in jsdom).
//
// The Customer↔Site link key differs by site kind:
//   • tank battery      → hauler_operators.id  (battery.haulerOperatorId === customer.value)
//   • standalone well   → platform_operators.id (well.operatorId === customer.operatorId)
//   • legacy company_site → dead (permanently empty; carries a legacy operators id
//                            with no hauler_operators mapping, so it drops out once
//                            a customer is chosen).

interface PickupBattery {
  readonly id: string;
  readonly name: string;
  readonly haulerOperatorId: string;
  readonly operatorName: string;
}

// quick 260717-kme — a standalone well pickup (a well with a directly-attached
// tank; no tank battery). `operatorId` is the PLATFORM operator id used to link
// the well to a Customer (hauler_operators.operatorId).
interface PickupWell {
  readonly id: string;
  readonly name: string;
  // ClickUp 86bc7quft — shown in the option label so a dispatcher who searched by API
  // number can confirm by eye that they matched the well they meant. Well names are not
  // unique; the API number is the identifier printed on the regulatory paperwork.
  readonly apiNumber: string;
  readonly operatorId: string | null;
  readonly operatorName: string | null;
}

interface PickupSite {
  readonly siteId: string;
  readonly siteName: string;
  readonly operatorId: string;
  readonly operatorName: string;
}

// A hauler_operators row reduced to what the Customer field needs.
interface CustomerRow {
  readonly id: string;
  readonly resolvedName: string;
  readonly operatorId: string | null;
  readonly isActive: boolean;
}

export interface CustomerOption {
  readonly value: string; // hauler_operators.id
  readonly label: string;
  readonly operatorId: string | null; // platform_operators.id (null for private customers)
}

export interface SiteOption {
  readonly value: string; // tb:/wl:/cs: prefixed
  readonly label: string; // site name only (the operator now lives in the Customer field)
  readonly haulerOperatorId: string | null; // tank-battery path
  readonly operatorId: string | null; // well/legacy path (platform / legacy operator id)
}

/** Active hauler operators as Customer options, A→Z by display name. */
export function buildCustomerOptions(operators: ReadonlyArray<CustomerRow>): CustomerOption[] {
  return operators
    .filter((o) => o.isActive)
    .map((o) => ({ value: o.id, label: o.resolvedName, operatorId: o.operatorId }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Build the Site options: tank batteries (new model), then standalone wells (new
 * model), then legacy company_sites. The label is just the site name — the
 * operator is shown/selected in the separate Customer field now. Values are
 * prefixed (tb:/wl:/cs:) so the submit handler routes each to the right payload.
 */
export function buildSiteOptions(
  batteries: ReadonlyArray<PickupBattery>,
  wells: ReadonlyArray<PickupWell>,
  sites: ReadonlyArray<PickupSite>,
): SiteOption[] {
  return [
    ...batteries.map((b) => ({
      value: `tb:${b.id}`,
      label: b.name,
      haulerOperatorId: b.haulerOperatorId,
      operatorId: null,
    })),
    ...wells.map((w) => ({
      value: `wl:${w.id}`,
      // 86bc7quft — "NAME · API" so an API-number search shows its own match term, and
      // two wells sharing a name stay distinguishable.
      label: w.apiNumber === '' ? w.name : `${w.name} · ${w.apiNumber}`,
      haulerOperatorId: null,
      operatorId: w.operatorId,
    })),
    ...sites.map((s) => ({
      value: `cs:${s.siteId}`,
      label: s.siteName,
      haulerOperatorId: null,
      operatorId: s.operatorId,
    })),
  ];
}

/**
 * Filter the Site options to a selected Customer. With no customer chosen EVERY
 * site shows (the field is never gated). A tank battery matches on the
 * hauler_operators id; a standalone well matches on the customer's platform
 * operator id. Legacy company_sites have no hauler_operators mapping and drop out
 * once a customer is chosen.
 */
export function filterSitesForCustomer(
  sites: ReadonlyArray<SiteOption>,
  customer: CustomerOption | null,
): SiteOption[] {
  if (customer === null) return [...sites];
  return sites.filter((s) => {
    if (s.value.startsWith('tb:')) return s.haulerOperatorId === customer.value;
    if (s.value.startsWith('wl:')) {
      return customer.operatorId !== null && s.operatorId === customer.operatorId;
    }
    return false; // cs: legacy — no hauler_operators mapping
  });
}

/**
 * Narrow the Site options by the typed needle (ClickUp 86bc7quft).
 *
 * The Site picker runs with cmdk's own filtering DISABLED, because cmdk matches the
 * visible label and would therefore discard a well the server found by API number.
 * With it off, nothing filters the other two sources — so this does, and only for them:
 *
 *   wl:  wells      — already filtered BY THE SERVER (name OR api_number), so they pass
 *                     through untouched. Re-matching them on the label here would
 *                     reintroduce exactly the bug this change removes.
 *   tb:  batteries  — local substring match. /v1/tank-batteries returns the tenant's
 *   cs:  legacy       full set with no cap, so there is nothing to page and no
 *                     truncation risk; a local match is correct and costs no request.
 *
 * An empty needle returns everything, which is the picker's just-opened state.
 */
export function filterSitesByNeedle(
  sites: ReadonlyArray<SiteOption>,
  needle: string,
): SiteOption[] {
  const n = needle.trim().toLowerCase();
  if (n === '') return [...sites];
  return sites.filter((s) => s.value.startsWith('wl:') || s.label.toLowerCase().includes(n));
}

/**
 * The Customer (hauler_operators.id) to autofill when a Site is selected:
 *   • tank battery    → its haulerOperatorId IS a hauler_operators id.
 *   • standalone well → the customer whose platform operatorId matches the well's.
 *   • legacy / no match → '' (leave the Customer field unset).
 */
export function customerIdForSite(
  siteValue: string,
  sites: ReadonlyArray<SiteOption>,
  customers: ReadonlyArray<CustomerOption>,
): string {
  const site = sites.find((s) => s.value === siteValue);
  if (site === undefined) return '';
  if (site.value.startsWith('tb:')) return site.haulerOperatorId ?? '';
  if (site.value.startsWith('wl:')) {
    if (site.operatorId === null) return '';
    return customers.find((c) => c.operatorId === site.operatorId)?.value ?? '';
  }
  return '';
}

export const SITE_NO_LOCATION_WARNING =
  "This site has no location yet, so the driver's arrival can't be GPS-checked. Place it on the Geofences map when you can.";

/**
 * DEC-353 (reverses DEC-319's block) — the warning shown under Site when the chosen
 * tank battery has no location (no pin and no located well). The job is still
 * created: the warning never disables Create Job. The map data (`GET /v1/sites`)
 * carries the battery's centre, pin or well average, so a null one is the case.
 * Null when there is nothing to warn about, or the battery is not in the list yet.
 */
export function siteLocationWarning(
  pickupSel: string,
  sites: ReadonlyArray<Pick<SiteSummary, 'siteId' | 'siteType' | 'centerLat' | 'centerLng'>>,
  well?: Pick<WellDetail, 'id' | 'wellheadLat' | 'wellheadLng'> | null,
): string | null {
  if (pickupSel.startsWith('wl:')) return wellLocationWarning(pickupSel.slice(3), well);
  if (!pickupSel.startsWith('tb:')) return null;
  const batteryId = pickupSel.slice(3);
  const battery = sites.find((s) => s.siteId === batteryId && s.siteType === 'TANK_BATTERY');
  if (battery === undefined) return null;
  return battery.centerLat === null || battery.centerLng === null ? SITE_NO_LOCATION_WARNING : null;
}

export const WELL_NO_LOCATION_WARNING =
  "This well has no location on file, so the driver's arrival can't be GPS-checked.";

/**
 * #51 — a standalone-well pickup is GPS-checked against the well's wellhead (the API's
 * services/location-centre.ts `wellCentreSql`), so a well with no usable wellhead reads
 * UNVERIFIABLE on arrival, exactly like an unplaced battery. Same rule as the server:
 * both coordinates present, and not (0, 0) — a missing value written as zero. Its own
 * wording, because a well's location comes from the platform well catalog, not the
 * Geofences map. A warning, never a block. Null while the well is not loaded yet (or is
 * another well), so nothing flashes before the data arrives.
 */
function wellLocationWarning(
  wellId: string,
  well: Pick<WellDetail, 'id' | 'wellheadLat' | 'wellheadLng'> | null | undefined,
): string | null {
  if (well == null || well.id !== wellId) return null;
  const lat = well.wellheadLat === null ? null : Number(well.wellheadLat);
  const lng = well.wellheadLng === null ? null : Number(well.wellheadLng);
  const usable =
    lat !== null &&
    lng !== null &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    !(lat === 0 && lng === 0);
  return usable ? null : WELL_NO_LOCATION_WARNING;
}

/** Upper bound on dispatcher notes — mirrors PATCH /v1/jobs/:id/notes and POST /v1/jobs. */
export const MAX_DISPATCHER_NOTES = 5000;

/**
 * #44 — the dispatcher notes for the create body: sent exactly as typed (free text,
 * not an identifier — DEC-147 notes exception), omitted when blank.
 */
export function dispatcherNotesPayload(notes: string): Pick<CreateJobBody, 'dispatcherNotes'> {
  return notes.trim() === '' ? {} : { dispatcherNotes: notes };
}

/**
 * Map a Site selection to the create-job payload fields:
 *   - tb:<id> → tankBatteryId (server resolves the operator + HaulId)
 *   - wl:<id> → pickupWellId (server resolves the operator + HaulId from the well)
 *   - cs:<id> → siteId + the site's operatorId (legacy old-model path)
 *   - anything else (empty) → no pickup fields
 */
export function resolvePickupPayload(
  siteValue: string,
  sites: ReadonlyArray<SiteOption>,
): { tankBatteryId?: string; pickupWellId?: string; siteId?: string; operatorId?: string } {
  if (siteValue.startsWith('tb:')) return { tankBatteryId: siteValue.slice(3) };
  if (siteValue.startsWith('wl:')) return { pickupWellId: siteValue.slice(3) };
  if (siteValue.startsWith('cs:')) {
    const csId = siteValue.slice(3);
    const site = sites.find((s) => s.value === siteValue);
    return site?.operatorId ? { siteId: csId, operatorId: site.operatorId } : { siteId: csId };
  }
  return {};
}

// The linked-field state (Customer id + Site selection) as a pair, so the two
// transitions below are pure and unit-testable without mounting the combobox.
export interface PickupSelection {
  readonly customerId: string;
  readonly pickupSel: string;
}

/**
 * Transition when the user picks a Customer: keep the customer and CLEAR the Site.
 * The Site list is re-filtered to the new customer, so a previously-selected site
 * may no longer belong — clearing it keeps the (Customer, Site) pair consistent.
 */
export function onCustomerSelected(customerId: string): PickupSelection {
  return { customerId, pickupSel: '' };
}

/**
 * Transition when the user picks a Site: keep the Site and AUTOFILL the Customer
 * from that site's operator (empty when it can't be resolved — e.g. a well whose
 * operator isn't in the customer list). The Site is never cleared here.
 */
export function onSiteSelected(
  siteValue: string,
  sites: ReadonlyArray<SiteOption>,
  customers: ReadonlyArray<CustomerOption>,
): PickupSelection {
  return { customerId: customerIdForSite(siteValue, sites, customers), pickupSel: siteValue };
}

const fieldStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '40px',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
};

// Custom up/down stepper for the "Number of loads" field — replaces the unstyled
// browser-default number spinners (hidden via .njm-number-no-spinner in styles.css)
// with a dark-themed control consistent with the rest of the form. Cosmetic only.
const stepButtonStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: 'none',
  backgroundColor: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-secondary)',
  cursor: 'pointer',
  fontSize: '9px',
  lineHeight: 1,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  marginBottom: '4px',
};

export function NewJobModal({ open, onOpenChange }: NewJobModalProps): React.JSX.Element {
  const apiOpts = useApiOpts();
  const queryClient = useQueryClient();

  const { data: driversData } = useDrivers(apiOpts);
  const { data: operatorsData } = useOperators(apiOpts);
  const { data: sitesData } = useSites();
  // Phase 24 / Task 24.10 (DEC-101) — the new-model pickup source. When the tenant
  // has ≥1 active tank battery the form switches to the battery path and hides the
  // legacy Producer/Site/Tank dropdowns; with none, it stays on the old model.
  const { data: batteriesData } = useTankBatteries();
  // The Customer field's source — the hauler's active operators (customers).
  const { data: haulerOpsData } = useHaulerOperators();

  const drivers = driversData?.drivers ?? [];
  const operators = operatorsData?.operators ?? [];
  const batteries = React.useMemo(
    () => (batteriesData?.tankBatteries ?? []).filter((b) => b.isActive),
    [batteriesData],
  );
  // Phase 26.2 / DEC-147 — `company_sites` was dropped and the `/v1/sites`
  // union's PICKUP arm is now permanently empty. This memo consequently
  // resolves to `[]` in production, and every non-battery branch below is
  // dead code that can be swept once we retire `buildPickupOptions`'s
  // `sites` parameter + `resolvePickupPayload`'s `cs:` branch + their tests.
  // Leaving in place for one more cutover cycle to keep the 26.6 wrap-up
  // diff docs-shaped (issue tracked in `gsd/BUILD_QUEUE.md` Phase 26 done
  // block; concrete follow-up owned by Phase 27 mobile cleanup).
  const pickupSites = React.useMemo(
    () =>
      (sitesData?.sites ?? []).filter((s) => (s.siteType ?? 'PICKUP') === 'PICKUP' && s.isActive),
    [sitesData],
  );
  // quick 260717-kme — standalone-well pickups. The hauler's catalog wells whose
  // `standaloneTank` is non-null (a well_id-set / hauler_location_id-NULL tank)
  // are pickable without a tank battery. `onlyMyCatalog` lists the bounded set
  // with no query term; the label shows the operator so the dispatcher sees it
  // before selecting (the server rejects OPERATOR_NOT_IN_CATALOG if the well's
  // operator isn't in the customer list).
  //
  // ClickUp 86bc7quft — the well list is SERVER-SEARCHED, not preloaded-and-filtered.
  // It used to call useWellSearch('', …) once and filter the result client-side forever,
  // which broke two ways:
  //   1. api_number was unsearchable. The server matches it (ilike on wells.api_number),
  //      but the client only ever filtered the fetched page by LABEL — and the label is
  //      the well name, so the one unambiguous identifier on regulatory paperwork found
  //      nothing.
  //   2. Truncation was undetectable. The preload caps at WELL_SEARCH_MAX_LIMIT with no
  //      count in the response, so a tenant with more catalog wells than the cap got a
  //      picker silently missing wells: typing a real well's name returned "no results"
  //      because it was never in the preload and no query was ever sent.
  // Empty query still preloads (the common small-catalog case reads instantly); typing
  // sends a debounced query, so the cap applies to the filtered set instead of the catalog.
  const [siteQuery, setSiteQuery] = React.useState('');
  const debouncedSiteQuery = useDebounced(siteQuery, 250);
  const { data: wellsData, isFetching: wellsFetching } = useWellSearch(
    debouncedSiteQuery,
    undefined,
    true,
  );
  // A result exactly at the cap is very likely truncated — the convention named in
  // @lodex-pro/shared (OPEN-053) precisely so a caller can tell a full page from a
  // complete answer. Surfaced in the picker footer rather than assumed complete.
  const wellsTruncated = (wellsData?.wells.length ?? 0) >= WELL_SEARCH_MAX_LIMIT;
  const pickupWells = React.useMemo(
    () =>
      (wellsData?.wells ?? [])
        // Only a well carrying a standalone tank is a valid pickup. The server search is
        // not aware of that, so it can return wells that are not selectable; filtering
        // here keeps the picker honest about what can actually be chosen.
        .filter((w) => w.standaloneTank !== null)
        .map((w) => ({
          id: w.id,
          name: w.wellName,
          apiNumber: w.apiNumber,
          operatorId: w.operatorId,
          operatorName: w.operatorName,
        })),
    [wellsData],
  );
  // Split pickup: the Customer options (hauler operators) and the Site options
  // (tank batteries + standalone wells + legacy company_sites). Site values are
  // prefixed (tb:/wl:/cs:) so handleSubmit routes each to the right payload.
  // §58 PR 4 — on EXPRESS only the one customer, and its pickup sites, are offered.
  const { express, pending: expressPending } = useExpressCustomerState();
  const lockedCustomerId = lockedExpressCustomerId(express);
  const customerOptions = React.useMemo(
    () => onlyExpressCustomers(buildCustomerOptions(haulerOpsData?.haulerOperators ?? []), express),
    [haulerOpsData, express],
  );
  const siteOptions = React.useMemo(
    () => onlyExpressPickupSites(buildSiteOptions(batteries, pickupWells, pickupSites), express),
    [batteries, pickupWells, pickupSites, express],
  );
  // New model applies when there is any new-model pickup source (battery or well).
  const useNewModel = batteries.length > 0 || pickupWells.length > 0;

  const [jobTypeId, setJobTypeId] = React.useState<string>('');
  const [operatorId, setOperatorId] = React.useState<string>('');
  const [siteId, setSiteId] = React.useState<string>('');
  // New-model split pickup. `customerId` is a hauler_operators id (the Customer
  // field); `pickupSel` is the Site selection — 'tb:<batteryId>' (tank battery),
  // 'wl:<wellId>' (standalone well), or 'cs:<siteId>' (legacy). The prefix routes
  // handleSubmit; the two are linked (see the Combobox onChange handlers below).
  const [customerId, setCustomerId] = React.useState<string>('');
  // §58 PR 4 — the one customer is preselected and the field locked.
  React.useEffect(() => {
    if (lockedCustomerId !== null && customerId === '') setCustomerId(lockedCustomerId);
  }, [lockedCustomerId, customerId]);
  const [pickupSel, setPickupSel] = React.useState<string>('');
  const [tankId, setTankId] = React.useState<string>('');
  const [destinationId, setDestinationId] = React.useState<string>('');
  // Phase 28 (DEC-331 D-14 A) — the typed reason for sending this customer's water to a
  // place on its "never send here" list. Required only while such a place is chosen.
  const [overrideReason, setOverrideReason] = React.useState<string>('');
  // Phase 28 (DEC-331) — every usable place, ranked for this pickup per the company's
  // dispatcher display mode (complete, so no 50-facility cut-off).
  const { data: recData, isError: recError } = useDisposalRecommendations(apiOpts, pickupSel);
  const pickupChosen = pickupSel.startsWith('tb:') || pickupSel.startsWith('wl:');
  const destinationOptions = React.useMemo(
    () => buildDestinationOptions(recData, pickupChosen),
    [recData, pickupChosen],
  );
  const excludedDestination = excludedChoice(recData, destinationId);
  const [driverId, setDriverId] = React.useState<string>('');
  const [scheduledAt, setScheduledAt] = React.useState<string>('');
  // DEC-146 (Phase 17) — flag this job urgent at creation.
  const [isUrgent, setIsUrgent] = React.useState<boolean>(false);
  // Quick 260722 — number of identical loads to create (kept as a string so the
  // field can be cleared while typing; clamped on submit).
  const [quantity, setQuantity] = React.useState<string>('1');
  // Legacy "New Request" parity — optional requester contact + reference id.
  const [requesterName, setRequesterName] = React.useState<string>('');
  const [requesterPhone, setRequesterPhone] = React.useState<string>('');
  const [requesterEmail, setRequesterEmail] = React.useState<string>('');
  const [referenceId, setReferenceId] = React.useState<string>('');
  const [dispatcherNotes, setDispatcherNotes] = React.useState<string>('');
  const [error, setError] = React.useState<string | null>(null);
  // #51 — the chosen standalone well's wellhead, for its no-location warning. The search
  // results carry no coordinates, so read the one selected well (cached per id).
  const { data: selectedWellData } = useWellDetail(
    pickupSel.startsWith('wl:') ? pickupSel.slice(3) : '',
  );
  const locationWarning = siteLocationWarning(
    pickupSel,
    sitesData?.sites ?? [],
    selectedWellData?.well ?? null,
  );

  // OPEN-053 — the requestors assigned to the chosen pickup location. Single-select: the
  // dispatcher ticks ONE of the (up to) two, or neither and types the free-text fields.
  const requestorTarget = assignmentTargetFor(pickupSel);
  const { data: requestorSlots } = useAssignments(
    requestorTarget?.targetType ?? 'TANK_BATTERY',
    requestorTarget?.targetId ?? null,
  );
  const requestorChoices = selectableRequestors(requestorSlots);
  const [requestorChoice, setRequestorChoice] = React.useState<string | null>(null);
  // Changing the pickup location invalidates the choice — the previous site's requestor is
  // not assigned to the new one, and the server would reject them. Keyed on the selection
  // string rather than the target object, which is rebuilt every render.
  React.useEffect(() => {
    setRequestorChoice(null);
  }, [pickupSel]);
  const lockRequesterFields = requesterFieldsLocked(requestorChoice);

  // JOBTYPE-13 — scope the Job Type list to the selected customer. The new
  // model's `customerId` IS a hauler_operators.id (the scoping table's key).
  // The legacy branch's `operatorId` is NOT the same identity — it sources
  // GET /v1/operators, the platform E&P producer catalog (use-operators.ts),
  // not GET /v1/hauler-operators — so scoping on it would filter against the
  // wrong id and silently hide job types with no visible cause. The legacy
  // branch therefore stays unscoped (today's behaviour, identical to
  // customerId === null) until it gets its own hauler-operators-backed field.
  const scopedCustomerId: string | null = useNewModel
    ? customerId === ''
      ? null
      : customerId
    : null;

  const { data: jobTypesData, isLoading: jobTypesLoading } = useJobTypesForCustomer(
    apiOpts,
    scopedCustomerId,
  );
  // JOBTYPE-14 / D-13 — LINE_ITEM job types have no capture path in 09.1 and
  // must not be dispatchable yet. The line-item capture screen ships in the
  // Supplier Tenant & Consumable Delivery phase (ClickUp 86baxtwc8).
  const selectableJobTypes = React.useMemo(
    () => (jobTypesData?.jobTypes ?? []).filter((jt) => jt.ticketShape !== 'LINE_ITEM'),
    [jobTypesData],
  );

  // Clear a job type selection that is no longer valid for the newly-selected
  // customer — leaving a stale id selected would submit a job type the
  // customer is not scoped to; the server would reject it and the dispatcher
  // would have no idea which field was wrong.
  React.useEffect(() => {
    if (jobTypeId === '') return;
    if (!selectableJobTypes.some((jt) => String(jt.id) === jobTypeId)) setJobTypeId('');
  }, [scopedCustomerId, selectableJobTypes, jobTypeId]);

  // DEC-176 (ClickUp 86bb0yq4b) — preselect the job type flagged isDefaultForDispatch
  // on open, ONLY while the field is still untouched. The ref makes this fire at most
  // once (after the list loads), so a dispatcher who clears or changes the selection is
  // never clobbered. Skips a default that isn't selectable (LINE_ITEM/out-of-scope).
  const didPreselectJobTypeRef = React.useRef(false);
  React.useEffect(() => {
    if (didPreselectJobTypeRef.current) return;
    if (jobTypeId !== '') {
      didPreselectJobTypeRef.current = true;
      return;
    }
    if (selectableJobTypes.length === 0) return; // still loading — retry on next render
    const pick = preselectDefaultJobTypeId(selectableJobTypes, jobTypeId);
    if (pick !== null) setJobTypeId(pick);
    didPreselectJobTypeRef.current = true;
  }, [selectableJobTypes, jobTypeId]);

  // #5 — SITE options are filtered to the selected producer's sites. Derived from
  // the stable query data (not a per-render array) so the memo deps stay stable.
  const sitesForOperator = React.useMemo(() => {
    const all = sitesData?.sites ?? [];
    // Wave 3 — deactivated sites are hidden from operational surfaces (is_active pattern).
    return operatorId ? all.filter((s) => s.operatorId === operatorId && s.isActive) : [];
  }, [sitesData, operatorId]);

  // New-model split pickup: the selected Customer, and the Site options filtered
  // to it (all sites when no customer is chosen — the field is never gated).
  const selectedCustomer = customerOptions.find((c) => c.value === customerId) ?? null;
  const filteredSiteOptions = React.useMemo(
    () => filterSitesForCustomer(siteOptions, selectedCustomer),
    [siteOptions, selectedCustomer],
  );
  // 86bc7quft — cmdk's filter is off for this picker (it would drop API-number matches),
  // so the needle is applied here: wells are already server-filtered, batteries and
  // legacy sites are matched locally. Uses the RAW query, not the debounced one, so
  // typing still feels instant for the local sources while the well request is in flight.
  const visibleSiteOptions = React.useMemo(
    () => filterSitesByNeedle(filteredSiteOptions, siteQuery),
    [filteredSiteOptions, siteQuery],
  );

  // Wave 2 (#3) — TANK is the third chained step; tanks are fetched only once a
  // site is selected (the endpoint already scopes to that site).
  const { data: tanksData } = useTanks(apiOpts, siteId);
  const tanks = tanksData?.tanks ?? [];

  const reset = React.useCallback((): void => {
    setJobTypeId('');
    setOperatorId('');
    setSiteId('');
    setCustomerId('');
    setPickupSel('');
    setTankId('');
    setDestinationId('');
    setOverrideReason('');
    setDriverId('');
    setScheduledAt('');
    setIsUrgent(false);
    setQuantity('1');
    setRequesterName('');
    setRequesterPhone('');
    setRequesterEmail('');
    setReferenceId('');
    setDispatcherNotes('');
    setRequestorChoice(null);
    setError(null);
  }, []);

  const mutation = useApiMutation<
    CreateJobBody,
    { job: { id: string }; jobs: Array<{ id: string; haulId: string | null }> }
  >(apiOpts, '/v1/jobs', 'POST', {
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
      // Quick 260722 — a multi-create confirmation lists every created ticket ID
      // (a generic "Job created" only reads right for a single ticket).
      const created = data.jobs ?? [];
      if (created.length > 1) {
        const ids = created.map((j) => j.haulId ?? '—').join(', ');
        toast.success(`${created.length.toString()} jobs created`, { description: ids });
      } else {
        toast.success('Job created');
      }
      reset();
      onOpenChange(false);
    },
    onError: (err: Error) => {
      // §58 — the server's one-customer refusal, in words (no plan name, Q14).
      setError(
        err instanceof ApiError && err.code === 'EXPRESS_CUSTOMER_ONLY'
          ? 'This company works for one customer. Choose that customer and one of its sites.'
          : 'Could not create the job. Check your inputs and try again.',
      );
    },
  });

  const handleSubmit = React.useCallback(
    (e: React.FormEvent): void => {
      e.preventDefault();
      setError(null);
      const parsedJobTypeId = Number(jobTypeId);
      if (!Number.isInteger(parsedJobTypeId) || parsedJobTypeId <= 0) {
        setError('Select a job type.');
        return;
      }
      const body: CreateJobBody = { jobTypeId: parsedJobTypeId };
      if (useNewModel) {
        // Split pickup: the Site selection drives the payload — a tank battery
        // sends tankBatteryId, a standalone well sends pickupWellId, a legacy
        // company_site sends siteId + its operatorId. The Customer field is a
        // filter/autofill aid; the server resolves the operator from the site.
        Object.assign(body, resolvePickupPayload(pickupSel, siteOptions));
      } else {
        if (operatorId) body.operatorId = operatorId;
        if (siteId) body.siteId = siteId;
        if (tankId) body.tankId = tankId;
      }
      Object.assign(body, destinationPayload(destinationId));
      if (excludedDestination !== null) {
        // D-14 A: allowed, but never silently — the reason is recorded and audited.
        const reason = overrideReason.trim();
        if (reason.length < MIN_OVERRIDE_REASON) {
          setError(
            `${excludedDestination.name} is on this customer's "never send here" list. Say why you are sending it there anyway.`,
          );
          return;
        }
        body.disposalOverrideReason = reason;
      }
      if (driverId) body.assignedDriverId = driverId;
      if (scheduledAt) {
        // datetime-local → ISO 8601 with offset (API requires offset:true).
        const iso = new Date(scheduledAt).toISOString();
        body.scheduledAt = iso;
      }
      if (isUrgent) body.isUrgent = true;
      // Clamp quantity to [1, MAX]; an empty/invalid field falls back to 1.
      const qty = clampQuantity(quantity);
      if (qty > 1) body.quantity = qty;
      // Legacy "New Request" parity — only non-empty (trimmed) optional contact /
      // reference values are sent; the server treats each as nullable.
      Object.assign(
        body,
        collectRequestMetadata({ requesterName, requesterPhone, requesterEmail, referenceId }),
      );
      // OPEN-053 — the chosen requestor, if any. `requestorPayload` OMITS the field when
      // nobody was picked (the body schema is .strict() with an optional uuid, so a literal
      // null would be rejected). The server snapshots their details over requester_*.
      Object.assign(body, requestorPayload(requestorChoice));
      Object.assign(body, dispatcherNotesPayload(dispatcherNotes));
      mutation.mutate(body);
    },
    [
      jobTypeId,
      useNewModel,
      operatorId,
      siteId,
      pickupSel,
      siteOptions,
      tankId,
      destinationId,
      excludedDestination,
      overrideReason,
      driverId,
      scheduledAt,
      isUrgent,
      quantity,
      requesterName,
      requesterPhone,
      requesterEmail,
      referenceId,
      requestorChoice,
      dispatcherNotes,
      mutation,
    ],
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next): void => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      {/* Widen past the shared DialogContent default (w-full max-w-lg) and cap the
          height so the now-long form (pickup [customer/site, or legacy
          producer/site/tank], job type, destination, driver, scheduled,
          reference id, requester contact, number of loads, urgent) scrolls
          INTERNALLY instead of overflowing the viewport — the footer buttons
          stay reachable. max-w-2xl (not JobDetailModal's max-w-4xl): this is a
          single-column form, not a wide detail grid. tailwind-merge keeps the
          later max-w/w utilities over the component default.

          JOBTYPE-14 / DEC-170 item 7 — Customer/Site are chosen BEFORE Job Type so the
          Job Type list can be narrowed to what that customer is allowed. DEC-170 names
          RunTicketScreen.tsx for this reorder; that file has no Customer/Site/Job Type
          picker at all (verified 2026-07-24, see 09.1-CONTEXT.md). This modal is the
          real target. */}
      <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Job</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
        >
          {useNewModel ? (
            /* New model — two linked, always-enabled fields: Customer then Site.
               Selecting a Customer filters the Site list to that customer;
               selecting a Site autofills its Customer. Neither is gated on the
               other. The server resolves the operator + HaulId from the Site. */
            <>
              <div>
                <label htmlFor="njm-customer" style={labelStyle}>
                  Customer
                </label>
                <Combobox
                  id="njm-customer"
                  value={customerId}
                  onChange={(v): void => {
                    const next = onCustomerSelected(v);
                    setCustomerId(next.customerId);
                    setPickupSel(next.pickupSel); // clears a now-mismatched site
                  }}
                  options={customerOptions.map((c) => ({ value: c.value, label: c.label }))}
                  disabled={lockedCustomerId !== null || expressPending}
                  placeholder="Unassigned"
                  searchPlaceholder="Search customers…"
                  emptyText="No customers match."
                />
              </div>

              <div>
                <label htmlFor="njm-site-new" style={labelStyle}>
                  Site
                </label>
                <Combobox
                  id="njm-site-new"
                  value={pickupSel}
                  onChange={(v): void => {
                    // Autofill the Customer from the chosen Site (pass the full,
                    // unfiltered site + customer lists so the lookup always resolves).
                    const next = onSiteSelected(v, siteOptions, customerOptions);
                    setCustomerId(next.customerId);
                    setPickupSel(next.pickupSel);
                  }}
                  options={visibleSiteOptions.map((s) => ({ value: s.value, label: s.label }))}
                  // §58 — until the profile says whether the company works for one customer,
                  // the site list is not limited yet: nothing can be picked from it.
                  disabled={expressPending}
                  placeholder="Unassigned"
                  searchPlaceholder="Search name or API number…"
                  // 86bc84c5y — when the well list came back capped, a bare "No sites
                  // match." is wrong: a match may well exist past the cap. Say so here,
                  // because the truncation footer is (correctly) suppressed while the
                  // list is empty.
                  emptyText={
                    wellsTruncated
                      ? `No match in the first ${WELL_SEARCH_MAX_LIMIT} wells — refine your search.`
                      : selectedCustomer
                        ? 'No sites for this customer.'
                        : 'No sites match.'
                  }
                  // 86bc7quft — the well list comes from the server, so cmdk must not
                  // filter it again: it matches on label, and would discard a well found
                  // by API number. filterSitesByNeedle handles the local sources instead.
                  onSearchChange={setSiteQuery}
                  shouldFilter={false}
                  loading={wellsFetching}
                  footer={
                    wellsTruncated ? (
                      <div
                        role="status"
                        style={{
                          padding: '8px 10px',
                          borderTop: '1px solid var(--color-border-secondary)',
                          fontSize: '12px',
                          color: 'var(--color-text-tertiary)',
                        }}
                      >
                        Showing the first {WELL_SEARCH_MAX_LIMIT} wells — refine your search to see
                        more.
                      </div>
                    ) : null
                  }
                />
                {/* DEC-353 — a warning, never a block: Create Job stays enabled. */}
                {locationWarning !== null ? (
                  <p
                    role="status"
                    style={{
                      marginTop: '8px',
                      marginBottom: 0,
                      padding: '8px 10px',
                      border: '1px solid var(--color-warning)',
                      borderRadius: '4px',
                      color: 'var(--color-warning)',
                      fontSize: '13px',
                    }}
                  >
                    {locationWarning}
                  </p>
                ) : null}
              </div>
            </>
          ) : (
            <>
              {/* Producer / Operator — typeahead; gates the SITE combobox (#5/#9) */}
              <div>
                <label htmlFor="njm-producer" style={labelStyle}>
                  Producer
                </label>
                <Combobox
                  id="njm-producer"
                  value={operatorId}
                  onChange={(v): void => {
                    setOperatorId(v);
                    setSiteId(''); // reset site when producer changes
                  }}
                  options={operators.map((o) => ({ value: o.id, label: o.name }))}
                  placeholder="Unassigned"
                  searchPlaceholder="Search producers…"
                  emptyText="No producers match."
                />
              </div>

              {/* Site — typeahead, filtered to the producer; disabled until one is chosen (#5/#9) */}
              <div>
                <label htmlFor="njm-site" style={labelStyle}>
                  Site
                </label>
                <Combobox
                  id="njm-site"
                  value={siteId}
                  onChange={(v): void => {
                    setSiteId(v);
                    setTankId(''); // reset tank when site changes (#3)
                  }}
                  options={sitesForOperator.map((s) => ({ value: s.siteId, label: s.siteName }))}
                  disabled={!operatorId}
                  placeholder={operatorId ? 'Unassigned' : 'Select a producer first'}
                  searchPlaceholder="Search sites…"
                  emptyText="No sites match."
                />
              </div>

              {/* Tank — typeahead, filtered to the site; disabled until a site is chosen (#3) */}
              <div>
                <label htmlFor="njm-tank" style={labelStyle}>
                  Tank
                </label>
                <Combobox
                  id="njm-tank"
                  value={tankId}
                  onChange={setTankId}
                  options={tanks.map((t) => ({ value: t.id, label: t.name }))}
                  disabled={!siteId}
                  placeholder={siteId ? 'Unassigned' : 'Select a site first'}
                  searchPlaceholder="Search tanks…"
                  emptyText="No active tanks for this site."
                />
              </div>
            </>
          )}

          {/* Job Type — required. JOBTYPE-14 / DEC-170 item 7 — Customer/Site are
              chosen BEFORE Job Type so the Job Type list can be narrowed to what
              that customer is allowed. DEC-170 names RunTicketScreen.tsx for this
              reorder; that file has no Customer/Site/Job Type picker at all
              (verified 2026-07-24, see 09.1-CONTEXT.md). This modal is the real
              target. */}
          <div>
            <label htmlFor="njm-jobtype" style={labelStyle}>
              Job Type *
            </label>
            <select
              id="njm-jobtype"
              value={jobTypeId}
              onChange={(e): void => {
                setJobTypeId(e.target.value);
              }}
              style={fieldStyle}
              required
              disabled={
                scopedCustomerId !== null && !jobTypesLoading && selectableJobTypes.length === 0
              }
            >
              <option value="">
                {jobTypesLoading
                  ? 'Loading job types…'
                  : scopedCustomerId !== null && selectableJobTypes.length === 0
                    ? 'No job types set up for this customer'
                    : 'Select a job type…'}
              </option>
              {selectableJobTypes.map((jt) => (
                <option key={jt.id} value={String(jt.id)}>
                  {jt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Destination — optional planned disposal place (260624-job-destination), ranked
              for the pickup per the company's display mode (Phase 28, DEC-331) */}
          <div>
            <label htmlFor="njm-destination" style={labelStyle}>
              Destination
            </label>
            <Combobox
              id="njm-destination"
              value={destinationId}
              onChange={setDestinationId}
              options={destinationOptions}
              placeholder="Unassigned"
              searchPlaceholder="Search disposal places…"
              emptyText="No disposal places match."
            />
            {recError ? (
              <p style={{ fontSize: '12px', color: 'var(--color-danger)', margin: '4px 0 0' }}>
                Could not load disposal places. Close and reopen to try again.
              </p>
            ) : null}
            {estimateNote(recData, pickupChosen) !== null ? (
              <p
                style={{
                  fontSize: '12px',
                  color: 'var(--color-text-secondary)',
                  margin: '4px 0 0',
                }}
              >
                {estimateNote(recData, pickupChosen)}
              </p>
            ) : null}
            {excludedDestination !== null ? (
              <div
                role="note"
                style={{
                  marginTop: '8px',
                  padding: '8px 10px',
                  border: '1px solid var(--color-danger)',
                  borderRadius: '4px',
                  color: 'var(--color-danger)',
                  fontSize: '13px',
                }}
              >
                <strong>Customer excluded.</strong> This customer asked never to send water to{' '}
                {excludedDestination.name}
                {excludedDestination.excludedReason !== null
                  ? `: ${excludedDestination.excludedReason}`
                  : ''}
                . You can still send it there; the reason is recorded and the choice is flagged.
                <label
                  htmlFor="njm-override-reason"
                  style={{ ...labelStyle, marginTop: '8px', color: 'var(--color-danger)' }}
                >
                  Why send it there anyway? (required)
                </label>
                <textarea
                  id="njm-override-reason"
                  value={overrideReason}
                  maxLength={500}
                  rows={2}
                  onChange={(e): void => setOverrideReason(e.target.value)}
                  style={{ ...fieldStyle, minHeight: '56px' }}
                />
              </div>
            ) : null}
          </div>

          {/* Driver — optional */}
          <div>
            <label htmlFor="njm-driver" style={labelStyle}>
              Driver
            </label>
            <select
              id="njm-driver"
              value={driverId}
              onChange={(e): void => {
                setDriverId(e.target.value);
              }}
              style={fieldStyle}
            >
              <option value="">Unassigned</option>
              {drivers.map((d) => (
                <option key={d.driverId} value={d.driverId}>
                  {d.driverName}
                </option>
              ))}
            </select>
          </div>

          {/* Scheduled + Reference ID — paired side by side on wide panels (both
              short); stacked on narrow ones. Reference ID: legacy "New Request"
              parity, optional PO / reference / order-number → jobs.reference_id
              (64-char max, mirrors the capture-time bound). */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="njm-scheduled" style={labelStyle}>
                Scheduled (optional)
              </label>
              <input
                id="njm-scheduled"
                type="datetime-local"
                value={scheduledAt}
                onChange={(e): void => {
                  setScheduledAt(e.target.value);
                }}
                style={fieldStyle}
              />
            </div>
            <div>
              <label htmlFor="njm-reference-id" style={labelStyle}>
                Reference ID (optional)
              </label>
              <input
                className="keep-case"
                id="njm-reference-id"
                type="text"
                maxLength={64}
                value={referenceId}
                onChange={(e): void => {
                  setReferenceId(e.target.value);
                }}
                placeholder="PO / reference / order number"
                style={fieldStyle}
              />
            </div>
          </div>

          {/* OPEN-053 — the requestors assigned to this pickup location. Rendered only when
              the location has any: a site with no assignments falls through to the free-text
              fields below, which is the day-one behaviour and the permanent fallback.
              Single-select — ticking the other one replaces the first. */}
          {requestorChoices.length > 0 && (
            <div>
              <span style={labelStyle}>Requestor</span>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  padding: '8px 10px',
                  border: '1px solid var(--color-border-secondary)',
                  borderRadius: '10px',
                  backgroundColor: 'var(--color-bg-tertiary)',
                }}
              >
                {requestorChoices.map((c) => (
                  <label
                    key={c.requestor.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={requestorChoice === c.requestor.id}
                      onChange={(): void => {
                        setRequestorChoice(toggleChoice(requestorChoice, c.requestor.id));
                      }}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span>{c.requestor.name}</span>
                    <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
                      {[c.requestor.company, c.requestor.phone]
                        .filter((v) => v !== null)
                        .join(' · ')}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Requester contact — legacy "New Request" parity. Optional; who asked
              for the haul + how to reach them. Free-text contact info (not an
              identifier), preserved as typed.

              Locked (not hidden) while a requestor is ticked: the server overwrites these
              with that person's details, so leaving them editable would show values that are
              about to be replaced. Untick to type someone else instead. */}
          {lockRequesterFields && (
            <p style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
              Contact details come from the selected requestor. Untick them to enter a different
              name.
            </p>
          )}
          <div>
            <label htmlFor="njm-requester-name" style={labelStyle}>
              Requester name (optional)
            </label>
            <input
              id="njm-requester-name"
              type="text"
              maxLength={200}
              value={requesterName}
              disabled={lockRequesterFields}
              onChange={(e): void => {
                setRequesterName(e.target.value);
              }}
              placeholder="Who requested this haul"
              style={{ ...fieldStyle, opacity: lockRequesterFields ? 0.5 : 1 }}
            />
          </div>
          {/* Phone + email paired on wide panels (both short); stacked on narrow. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="njm-requester-phone" style={labelStyle}>
                Requester phone (optional)
              </label>
              <input
                id="njm-requester-phone"
                type="tel"
                maxLength={50}
                value={requesterPhone}
                disabled={lockRequesterFields}
                onChange={(e): void => {
                  setRequesterPhone(e.target.value);
                }}
                placeholder="Contact phone"
                style={{ ...fieldStyle, opacity: lockRequesterFields ? 0.5 : 1 }}
              />
            </div>
            <div>
              <label htmlFor="njm-requester-email" style={labelStyle}>
                Requester email (optional)
              </label>
              <input
                id="njm-requester-email"
                type="email"
                maxLength={254}
                value={requesterEmail}
                disabled={lockRequesterFields}
                onChange={(e): void => {
                  setRequesterEmail(e.target.value);
                }}
                placeholder="Contact email"
                style={{ ...fieldStyle, opacity: lockRequesterFields ? 0.5 : 1 }}
              />
            </div>
          </div>

          {/* Number of loads (Quick 260722, batch-create N) + Urgent (DEC-146) —
              two small creation options paired on wide panels; items-end aligns the
              Urgent checkbox with the number input row. Stacked on narrow panels. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
            <div>
              <label htmlFor="njm-quantity" style={labelStyle}>
                Number of loads
              </label>
              {/* The native number spinners are hidden (.njm-number-no-spinner) and
                  replaced by the dark-themed up/down stepper so this field matches the
                  rest of the form. clampQuantity + min/max are unchanged — the stepper
                  and onBlur both route through clampQuantity. */}
              <div style={{ display: 'flex', alignItems: 'stretch' }}>
                <input
                  id="njm-quantity"
                  type="number"
                  min={1}
                  max={MAX_JOB_QUANTITY}
                  step={1}
                  value={quantity}
                  onChange={(e): void => {
                    setQuantity(e.target.value);
                  }}
                  onBlur={(): void => {
                    // Normalize on blur so the field never shows an out-of-range value.
                    const n = clampQuantity(quantity);
                    setQuantity(String(n));
                  }}
                  aria-label="Number of loads to create"
                  className="njm-number-no-spinner"
                  style={{
                    ...fieldStyle,
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    borderRight: 'none',
                  }}
                />
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '30px',
                    flexShrink: 0,
                    border: '1px solid var(--color-border-secondary)',
                    borderTopRightRadius: '4px',
                    borderBottomRightRadius: '4px',
                    overflow: 'hidden',
                  }}
                >
                  <button
                    type="button"
                    aria-label="Increase number of loads"
                    tabIndex={-1}
                    onClick={(): void => {
                      setQuantity(String(clampQuantity(String((Number(quantity) || 1) + 1))));
                    }}
                    style={stepButtonStyle}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    aria-label="Decrease number of loads"
                    tabIndex={-1}
                    onClick={(): void => {
                      setQuantity(String(clampQuantity(String((Number(quantity) || 1) - 1))));
                    }}
                    style={{
                      ...stepButtonStyle,
                      borderTop: '1px solid var(--color-border-secondary)',
                    }}
                  >
                    ▼
                  </button>
                </div>
              </div>
            </div>
            {/* Amber-badged on the board; fires the immediate completion email. */}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                minHeight: '40px',
              }}
            >
              <input
                type="checkbox"
                checked={isUrgent}
                onChange={(e): void => {
                  setIsUrgent(e.target.checked);
                }}
                aria-label="Mark job urgent"
              />
              <span
                style={{ fontSize: '13px', color: 'var(--color-text-primary)', fontWeight: 600 }}
              >
                Urgent
              </span>
            </label>
          </div>

          {/* #44 — free-text notes for the job (jobs.dispatcher_notes); the same field
              Job Detail edits later. Kept as typed, not uppercased (DEC-147 exception). */}
          <div>
            <label htmlFor="njm-dispatcher-notes" style={labelStyle}>
              Dispatcher notes (optional)
            </label>
            <textarea
              id="njm-dispatcher-notes"
              value={dispatcherNotes}
              maxLength={MAX_DISPATCHER_NOTES}
              rows={3}
              onChange={(e): void => {
                setDispatcherNotes(e.target.value);
              }}
              placeholder="Gate codes, directions, anything the driver should know"
              style={{ ...fieldStyle, minHeight: '72px', resize: 'vertical' }}
            />
          </div>

          {error && (
            <p role="alert" style={{ fontSize: '13px', color: 'var(--color-danger)', margin: 0 }}>
              {error}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={(): void => {
                reset();
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending || expressPending}
              style={{
                backgroundColor: 'var(--color-accent-blue)',
                color: 'var(--color-on-accent)',
                borderColor: 'var(--color-accent-blue)',
              }}
            >
              {mutation.isPending ? 'Creating…' : 'Create Job'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
